import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, ImageIcon, ShieldCheck, Save, Lock, Loader2 } from "lucide-react";

import "./SponsorEdit.css";

// Static brand data for demo
const STATIC_BRANDS = {
  TechCorp: {
    name: "TechCorp",
    logo: "https://via.placeholder.com/200x200/C6A87C/0A1D1A?text=TechCorp",
    overview:
      "TechCorp is building the next generation of enterprise AI tools. We help companies automate workflows, analyze data at scale, and make better decisions faster.\n\nOur platform integrates with existing systems and provides real-time insights across sales, marketing, and operations. Trusted by Fortune 500 companies worldwide.",
    value_prop: "Enterprise AI that actually works with your existing stack",
    video_url: "https://youtu.be/dQw4w9WgXcQ",
    presentation_url: "https://pitch.com/techcorp-deck",
    website: "https://techcorp.ai",
    contact_email: "partnerships@techcorp.ai",
    phone: "+1-555-0100",
    contact_phone: "+1-555-0100",
    linkedin: "https://linkedin.com/company/techcorp",
  },
  Greentide: {
    name: "Greentide",
    logo: "https://via.placeholder.com/200x200/7BC8A6/0A1D1A?text=Greentide",
    overview:
      "Greentide finances climate infrastructure at scale. We connect institutional capital with verified carbon projects, renewable energy developments, and sustainable supply chains.\n\nOur fund has deployed over $2B across 40+ countries, reducing 15M tons of CO₂ annually.",
    value_prop: "Climate capital that scales impact and returns",
    video_url: "https://vimeo.com/123456789",
    presentation_url: "",
    website: "https://greentide.io",
    contact_email: "invest@greentide.io",
    phone: "+44 20 7946 0958",
    contact_phone: "+44 20 7946 0958",
    linkedin: "https://linkedin.com/company/greentide",
  },
  "Venture Fund": {
    name: "Venture Fund",
    logo: "https://via.placeholder.com/200x200/D9BC93/0A1D1A?text=VF",
    overview:
      "Venture Fund backs technical founders building category-defining companies. We invest at Seed and Series A in enterprise software, fintech, and infrastructure.\n\nOur portfolio includes 12 unicorns and companies valued at $50B+ combined. We provide hands-on support with GTM, hiring, and follow-on fundraising.",
    value_prop: "Early-stage capital + operator experience",
    video_url: "",
    presentation_url: "https://docsend.com/view/vf-2024",
    website: "https://venturefund.com",
    contact_email: "hello@venturefund.com",
    phone: "+1-650-555-0200",
    contact_phone: "+1-650-555-0200",
    linkedin: "https://linkedin.com/company/venture-fund",
  },
};

/**
 * Sponsor self-edit page. Magic-link URL pattern:
 *   /sponsors/edit?company=TechCorp&token=<90-day-token>
 */
export default function SponsorEdit() {
  const [params] = useSearchParams();
  const company = params.get("company") || "";
  const token = params.get("token") || "";

  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState({
    logo: "",
    overview: "",
    value_prop: "",
    video_url: "",
    presentation_url: "",
    website: "",
    contact_email: "",
    contact_phone: "",
    linkedin: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;

    setTimeout(() => {
      const foundBrand = STATIC_BRANDS[company];

      if (foundBrand) {
        setBrand(foundBrand);
        setForm((f) => ({
          ...f,
          logo: foundBrand.logo || "",
          overview: foundBrand.overview || "",
          value_prop: foundBrand.value_prop || "",
          video_url: foundBrand.video_url || "",
          presentation_url: foundBrand.presentation_url || "",
          website: foundBrand.website || "",
          contact_email: foundBrand.contact_email || "",
          contact_phone: foundBrand.phone || foundBrand.contact_phone || "",
          linkedin: foundBrand.linkedin || "",
        }));
      } else {
        setBrand({
          error: "Brand not found yet — saving below will create it.",
        });
      }
    }, 400);
  }, [company]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = (file) => {
    if (!file) return;
    if (file.type !== "image/png") {
      return toast.error(
        "Logo must be a PNG (transparent background recommended)."
      );
    }
    if (file.size > 3 * 1024 * 1024) {
      return toast.error("Logo must be under 3 MB.");
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      try {
        const result = String(reader.result || "");
        set("logo", result);
        toast.success("Logo uploaded.");
      } catch {
        toast.error("Upload failed.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!token || !company) {
      setErr("Magic link is incomplete.");
      return;
    }

    setBusy(true);
    setErr("");

    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      const updatedBrand = {
        name: company,
        ...form,
      };

      setBrand(updatedBrand);
      toast.success("Profile saved.");
    } catch {
      const detail = "Save failed.";
      setErr(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!company || !token) {
    return (
      <div className="container-x sponsorEditMissingWrap">
        <div
          className="glass sponsorEditMissingCard"
          data-testid="sponsor-edit-missing-token"
        >
          <p className="overline sponsorEditMissingOverline">
            <Lock size={13} /> Missing magic link
          </p>
          <h2 className="font-display sponsorEditMissingTitle">
            This editor link is incomplete.
          </h2>
          <p className="sponsorEditMissingBody">
            Open the link sent to your sponsor contact email. Need a new one?
            Reach out to a vmeetio steward and we'll re-issue it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x sponsorEditPage" data-testid="sponsor-edit-page">
      <div className="sponsorEditInner">
        <p className="overline sponsorEditTopOverline">
          <ShieldCheck size={13} /> Sponsor self-edit
        </p>

        <h1 className="font-display sponsorEditTitle">{company}</h1>

        <p className="sponsorEditIntro">
          Refresh your logo (PNG only), about copy, video, slides/deck and
          contact details whenever you want. Saved instantly. Anyone with this
          link can edit — keep it private.
        </p>

        {err && (
          <p className="sponsorEditError" data-testid="sponsor-edit-error">
            {err}
          </p>
        )}

        <form onSubmit={save} className="sponsorEditForm">
          {/* Logo (PNG only) */}
          <div className="glass sponsorEditCard">
            <p className="overline sponsorEditCardOverline">Logo · PNG only</p>
            <p className="sponsorEditCardHint">
              Upload a transparent PNG so it reads on dark cards and gold chips.
              Max 3 MB.
            </p>

            <div className="sponsorEditLogoRow">
              {form.logo ? (
                <img
                  src={form.logo}
                  alt={`${company} logo`}
                  className="sponsorEditLogoImg"
                />
              ) : (
                <div className="sponsorEditLogoPlaceholder">
                  <ImageIcon size={22} />
                </div>
              )}

              <label className="sponsorEditUploadBtn">
                {uploading ? (
                  <Loader2 size={14} className="sponsorEditSpin" />
                ) : (
                  <Upload size={14} />
                )}
                {uploading ? "Uploading…" : form.logo ? "Replace PNG" : "Upload PNG"}

                <input
                  type="file"
                  accept="image/png"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  className="sponsorEditHiddenInput"
                  data-testid="sponsor-edit-logo-input"
                />
              </label>

              {form.logo && (
                <button
                  type="button"
                  onClick={() => set("logo", "")}
                  className="sponsorEditRemoveBtn"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* About */}
          <div className="glass sponsorEditCard sponsorEditCardStack">
            <div>
              <label className="overline sponsorEditLabel">Tagline (one line)</label>
              <input
                value={form.value_prop}
                onChange={(e) => set("value_prop", e.target.value)}
                data-testid="sponsor-edit-value-prop"
                placeholder="What you do, in one sentence"
                className="sponsorEditInput"
              />
            </div>

            <div>
              <label className="overline sponsorEditLabel">About</label>
              <textarea
                rows={5}
                value={form.overview}
                onChange={(e) => set("overview", e.target.value)}
                data-testid="sponsor-edit-overview"
                placeholder="Two-to-three short paragraphs."
                className="sponsorEditTextarea"
              />
            </div>
          </div>

          {/* Media */}
          <div className="glass sponsorEditCard sponsorEditMediaGrid">
            <div>
              <label className="overline sponsorEditLabel">Short video (URL)</label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => set("video_url", e.target.value)}
                data-testid="sponsor-edit-video"
                placeholder="https://youtu.be/... or https://vimeo.com/..."
                className="sponsorEditInput"
              />
            </div>

            <div>
              <label className="overline sponsorEditLabel">Slides / deck (URL)</label>
              <input
                type="url"
                value={form.presentation_url}
                onChange={(e) => set("presentation_url", e.target.value)}
                data-testid="sponsor-edit-presentation"
                placeholder="Link to a PDF, Google Slides, etc."
                className="sponsorEditInput"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="glass sponsorEditCard sponsorEditCardStack">
            <p className="overline">Contact details</p>

            <div className="sponsorEditContactGrid">
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                data-testid="sponsor-edit-contact-email"
                placeholder="hello@yourcompany.com"
                className="sponsorEditInput"
              />
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)}
                data-testid="sponsor-edit-contact-phone"
                placeholder="+44 7…"
                className="sponsorEditInput"
              />
              <input
                type="url"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                data-testid="sponsor-edit-website"
                placeholder="https://yourcompany.com"
                className="sponsorEditInput"
              />
              <input
                type="url"
                value={form.linkedin}
                onChange={(e) => set("linkedin", e.target.value)}
                data-testid="sponsor-edit-linkedin"
                placeholder="https://linkedin.com/company/..."
                className="sponsorEditInput"
              />
            </div>
          </div>

          <div className="sponsorEditActions">
            <button
              type="submit"
              disabled={busy}
              data-testid="sponsor-edit-save"
              className="btn-gold sponsorEditSaveBtn"
            >
              <Save size={14} /> {busy ? "Saving…" : "Save profile"}
            </button>

            <span className="sponsorEditFootnote">
              Public profile updates instantly at{" "}
              <code className="sponsorEditPath">/brands/{company}</code>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}